import type { FilterValueStatus } from "@octopusdeploy/octopus-server-client";
import { FilterValueStatusValues } from "@octopusdeploy/octopus-server-client";
import { createQueryParam, numberQueryParam, optionalStringQueryParam, useQueryStringParams } from "@octopusdeploy/portal-routes";
import { useMemo } from "react";
import type { ProjectTenantVariablesFilterState } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
export function useProjectTemplatesFilterState(): [
    ProjectTenantVariablesFilterState,
    (filter: ProjectTenantVariablesFilterState) => void
] {
    const searchParameter = optionalStringQueryParam("search");
    const environmentParameter = optionalStringQueryParam("environment");
    const tenantParameter = optionalStringQueryParam("tenant");
    const tagsParameter = optionalStringQueryParam("tags");
    const valueStatusParameter = createQueryParam("valueStatus", validateValueStatus, (v) => v);
    const pageNumberParameter = numberQueryParam("pageNumber");
    const templateIdParameter = optionalStringQueryParam("templateId");
    const [filter, setFilter] = useQueryStringParams([searchParameter, environmentParameter, tenantParameter, tagsParameter, valueStatusParameter, templateIdParameter, pageNumberParameter]);
    function setProjectTenantVariablesFilterState(filter: ProjectTenantVariablesFilterState) {
        setFilter({
            search: filter.filterByName,
            environment: filter.filterByEnvironments && filter.filterByEnvironments.length > 0 ? filter.filterByEnvironments.join(",") : undefined,
            tenant: filter.filterByTenants && filter.filterByTenants.length > 0 ? filter.filterByTenants.join(",") : undefined,
            tags: filter.filterByTags.length > 0 ? filter.filterByTags.join(",") : undefined,
            valueStatus: filter.filterByValueStatus,
            pageNumber: filter.pageNumber,
            templateId: filter.filterByTemplateId,
        });
    }
    const projectTenantVariablesFilterState = useMemo((): ProjectTenantVariablesFilterState => ({
        filterByName: filter.search,
        filterByEnvironments: filter.environment ? filter.environment.split(",") : [],
        filterByTenants: filter.tenant ? filter.tenant.split(",") : [],
        filterByTags: filter.tags ? filter.tags.split(",") : [],
        filterByValueStatus: filter.valueStatus,
        filterByTemplateId: filter.templateId,
        filterByLibraryVariableSetIds: [],
        filterByProjectIds: [],
        pageNumber: filter.pageNumber ?? 1,
    }), [filter.search, filter.environment, filter.tenant, filter.tags, filter.valueStatus, filter.pageNumber, filter.templateId]);
    return [projectTenantVariablesFilterState, setProjectTenantVariablesFilterState];
}
function validateValueStatus(valueStatus: string | undefined): FilterValueStatus | undefined {
    if (valueStatus === undefined)
        return undefined;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const possibleStatus = valueStatus as FilterValueStatus;
    return Object.values(FilterValueStatusValues).includes(possibleStatus) ? possibleStatus : undefined;
}
